import { FunctionComponent, useState, useEffect } from "react";
import TextOptionBoxes, {
  TextOptionBoxesData,
} from "../TextOptionBox/TextOptionBoxes";
import OptionBoxes, { OptionBoxesData } from "../OptionBox/OptionBoxes";
import IconOptionBoxes, {
  IconOptionBoxesData,
} from "../IconOptionBox/IconOptionBoxes";
import TextFields from "../TextField/TextFields";
import { OptionBoxProps } from "../OptionBox/OptionBox";
import OptIn from "../OptIn/OptIn";
import dayjs from "dayjs";
import "dayjs/locale/de";
import { validate } from "../../../lib/validations/validate";
type PageWrapperProps = {
  className?: string;
  data: OptionBoxesData | IconOptionBoxesData | TextOptionBoxesData | any;
  index: number;
  activeIndex: number;
  activePage: string;
  nextPage: string;
  previousPage: string;
  setActivePage: Function;
  setAnswers: Function;
  setAnswersAndPublish: Function;
  setScore: Function;
  answers?: any;
  score: { [key: string]: any };
  isLoading: boolean;
};

type FormData = {
  type: string;
  page: {
    type: string;
    id: string;
  };
  data: {
    [key: string]: {
      id: string;
      label?: string;
      value?: any;
    };
  };
};

const PageWrapper: FunctionComponent<PageWrapperProps> = ({
  data,
  index,
  activeIndex,
  activePage,
  nextPage,
  previousPage,
  setActivePage,
  setAnswers,
  setAnswersAndPublish,
  setScore,
  answers,
  score,
  isLoading,
}) => {
  const [transition, setTransition] = useState(
    activePage !== data.id ? "hidden" : "block"
  );
  const [formErrors, setFormErrors] = useState({});
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (activePage === data.id) {
      if (transition !== "block") {
        timeout = setTimeout(() => setTransition("block opacity-100"), 300);
      }
    } else {
      if (transition !== "hidden") {
        setTransition(
          `${index - activeIndex > 0 ? "" : "-"}translate-x-full opacity-0`
        );
        timeout = setTimeout(() => setTransition("hidden"), 300);
      }
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [activePage !== data.id]);

  useEffect(() => {
    setFormErrors({});
  }, [activeIndex]);

  const onDataSelected = async (formData: FormData) => {
    let errors: { [key: string]: string } = {};
    // Check Page Validations – if error, set form error
    const hasData = formData.data && Object.keys(formData.data).length > 0;
    if (data.validations.required) {
      if (!hasData) {
        errors.general = "Ups... Du hast vergessen etwas auszuwählen";
        // If page is multiple choice return – Else continue to check and display each inputs' validations
        if (formData.type !== "multiple_choice") {
          return;
        }
      } else {
        delete errors.general;
      }
    }
    // Check each component validations – If error, set component error
    if (hasData) {
      let validations: { [key: string]: any } = {};
      let hasValidations = false;
      data.components.forEach((el: any) => {
        if (el.validations) {
          hasValidations = true;
          validations[el.id] = el.validations;
        }
      });
      if (hasValidations) {
        errors = validate(formData.data, validations);
      }
    }
    // If error set errors else continue
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    } else {
      setFormErrors({});
    }

    // If addToScore is true, add answer value to score
    if (data.addToScore) {
      let scoreToAdd = 0;

      Object.values(formData.data).forEach((el) => {
        if (el) {
          scoreToAdd += el.value;
        }
      });
      setScore((current: { [key: string]: any }) => ({
        ...current,
        [data.id]: scoreToAdd,
      }));
    }
    // Set answers and check if you should publish
    const answerData = {
      [data.id]: {
        ...formData,
        encrypt: !!data.encrypt,
        title: data.heading,
        description: data.description,
      },
    };

    if (data.type === "opt-in" || !nextPage) {
      await setAnswersAndPublish(answerData);
    } else {
      setAnswers(answerData);
      setActivePage(nextPage);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onBackButtonPressed = () => {
    setActivePage(previousPage);
  };

  let content;
  if (data.type === "text_multiple_choice") {
    content = (
      <TextOptionBoxes
        data={data}
        nextPage={nextPage}
        onDataSelected={onDataSelected}
        onBack={index === 0 ? undefined : onBackButtonPressed}
        errors={formErrors}
        isActive={activeIndex === index}
        isLoading={isLoading}
      />
    );
  } else if (data.type === "list_single_choice") {
    content = (
      <OptionBoxes
        data={data}
        nextPage={nextPage}
        onDataSelected={onDataSelected}
        onBack={index === 0 ? undefined : onBackButtonPressed}
        errors={formErrors}
        isLoading={isLoading}
      />
    );
  } else if (data.type === "icon_multiple_choice") {
    content = (
      <IconOptionBoxes
        data={data}
        nextPage={nextPage}
        onDataSelected={onDataSelected}
        onBack={index === 0 ? undefined : onBackButtonPressed}
        errors={formErrors}
        isActive={activeIndex === index}
        isLoading={isLoading}
        answers={answers}
      />
    );
  } else if (data.type === "input") {
    content = (
      <TextFields
        data={data}
        nextPage={nextPage}
        onDataSelected={onDataSelected}
        onBack={index === 0 ? undefined : onBackButtonPressed}
        errors={formErrors}
        isActive={activeIndex === index}
        isLoading={isLoading}
      />
    );
  } else if (data.type === "opt-in") {
    content = (
      <OptIn
        data={data}
        onDataSelected={onDataSelected}
        onBack={index === 0 ? undefined : onBackButtonPressed}
        errors={formErrors}
        answers={answers}
        score={score}
        isLoading={isLoading}
      />
    );
  }
  let notes = [];
  if (data.notes) {
    notes = data.notes.map((el: string) => {
      return <p className="text-xs text-gray-500 mb-4">{el}</p>;
    });
  }
  return (
    <div
      className={`w-full flex flex-col max-w-5xl mx-auto items-center duration-300 transform ${transition}`}
      id={`page-wrapper-${data.id}`}
    >
      {content}
      <div className="mt-8 max-w-xl">{notes}</div>
    </div>
  );
};

export default PageWrapper;
